'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from './use-toast';

// ============================================================================
// TYPES
// ============================================================================

export interface EvaluationFilters {
  page?: number;
  page_size?: number;
  status_code?: string;
}

export interface Evaluation {
  id: string;
  eval_number: string;
  registration_number: string;
  brand: string | null;
  model: string | null;
  year: number | null;
  odometer_km: number | null;
  color: string | null;
  fuel_type: string | null;
  status_code: string;
  eval_date: string;
  web_search_enabled: boolean;
  selected_provider_codes: string[];
  customer_name: string | null;
  customer_phone: string | null;
  customer_expected_price: string | null;
  consolidation_retry_count: number;
  created_at: string;
  updated_at: string;
}

export interface EvaluationListResponse {
  items: Evaluation[];
  total: number;
  page: number;
  page_size: number;
}

export interface ProviderResult {
  id: string;
  provider_code: string;
  status_code: string;
  confidence_score: string | null;
  error_message: string | null;
  model_version: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  created_at: string;
}

export interface ConsolidatedResult {
  id: string;
  providers_included: number;
  providers_excluded: number;
  composite_risk_score: string | null;
  confidence_score: string | null;
  dealer_purchase_price_min: string | null;
  dealer_purchase_price_max: string | null;
  customer_sale_price_min: string | null;
  customer_sale_price_max: string | null;
  created_at: string;
}

export interface EvaluationReport extends Evaluation {
  consolidated_result: ConsolidatedResult | null;
  provider_results: ProviderResult[];
}

export interface CreateEvaluationPayload {
  registration_number: string;
  brand?: string;
  model?: string;
  year?: number;
  odometer_km?: number;
  color?: string;
  fuel_type?: string;
  transmission?: string;
  condition_code?: string;
  accident_history_code?: string;
  service_history_code?: string;
  ownership_count_code?: string;
  usage_type_code?: string;
  insurance_status_code?: string;
  cng_kit?: boolean;
  hypothecation?: boolean;
  emission_cert_valid?: boolean;
  idv_amount?: string;
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  customer_expected_price?: string;
  web_search_enabled?: boolean;
  selected_provider_codes?: string[];
  inspection_notes?: string;
}

// ============================================================================
// FETCH HELPERS
// ============================================================================

async function fetchEvaluations(filters: EvaluationFilters = {}): Promise<EvaluationListResponse> {
  const params = new URLSearchParams();
  if (filters.page) params.append('page', String(filters.page));
  if (filters.page_size) params.append('page_size', String(filters.page_size));
  if (filters.status_code) params.append('status_code', filters.status_code);

  const url = `/api/evaluator/evaluations${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch evaluations');
  return response.json() as Promise<EvaluationListResponse>;
}

async function fetchEvaluation(id: string): Promise<EvaluationReport> {
  const response = await fetch(`/api/evaluator/evaluations/${id}`);
  if (!response.ok) {
    if (response.status === 404) throw new Error('Evaluation not found');
    throw new Error('Failed to fetch evaluation');
  }
  return response.json() as Promise<EvaluationReport>;
}

async function createEvaluation(payload: CreateEvaluationPayload): Promise<Evaluation> {
  const response = await fetch('/api/evaluator/evaluations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json() as { detail?: string };
    throw new Error(err.detail ?? 'Failed to create evaluation');
  }
  return response.json() as Promise<Evaluation>;
}

// ============================================================================
// HOOKS
// ============================================================================

export function useEvaluations(filters: EvaluationFilters = {}) {
  return useQuery({
    queryKey: ['evaluations', filters],
    queryFn: () => fetchEvaluations(filters),
  });
}

export function useEvaluation(id: string) {
  return useQuery({
    queryKey: ['evaluation', id],
    queryFn: () => fetchEvaluation(id),
    enabled: Boolean(id),
  });
}

export function useCreateEvaluation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: createEvaluation,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['evaluations'] });
      toast({ title: 'Evaluation created', description: 'The pipeline has been queued.' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to create evaluation', description: error.message, variant: 'destructive' });
    },
  });
}
